import React, { useState, useMemo, useCallback } from 'react';
import { TouchableOpacity, ScrollView, ActivityIndicator, RefreshControl, Platform } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { ArrowLeft } from 'lucide-react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '@/types/navigation';
import { useQueries, useQueryClient } from '@tanstack/react-query';

// Types
import type { RootStackScreenProps } from '@/types/navigation';
import type { MeetingAnnouncementApplicationView } from '@/api/types';

// Components
import { Screen, ScreenHeader, SvgBackground } from '@/components';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';
import { AnnouncementCard } from './AnnouncementCard';
import { FilterButton } from './FilterButton';

// API
import { useMyMeetingAnnouncements } from '@/api/hooks/useMeetingAnnouncements';
import apiClient from '@/api/client';
import { extractCollectionItems, ApiPlatformCollectionResponse } from '@/api/types';
import {
  mapAnnouncementToCard,
  isAnnouncementActive,
  isAnnouncementArchived,
  AnnouncementApplicationCounts,
} from '@/api/mappers/announcementMappers';
import { useApplicationNotifications } from '@/hooks/useApplicationNotifications';

// Constants
import { COLORS } from '@/constants/colors';

type Props = RootStackScreenProps<'MyAnnouncements'>;

// Type for application query result
type ApplicationQueryResult = {
  announcementId: string;
  applications: MeetingAnnouncementApplicationView[];
};

const MyAnnouncementsScreen: React.FC<Props> = ({ navigation: rootNavigation }) => {
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const isMobileLayout = useIsMobileLayout();
  const queryClient = useQueryClient();

  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';
  const [activeFilter, setActiveFilter] = useState<'active' | 'archive'>('active');
  
  // Get new applications count per announcement
  const { getNewCountForAnnouncement } = useApplicationNotifications();

  // Navigation for detail screen
  const rootNav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  // Fetch user's announcements from API
  const {
    data: apiAnnouncements = [],
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useMyMeetingAnnouncements();

  // Fetch applications for all announcements in parallel
  // Use a different query key to avoid conflict with useAnnouncementApplications
  const applicationQueries = useQueries({
    queries: apiAnnouncements.map((announcement) => ({
      queryKey: ['applicationCounts', announcement.id] as const,
      queryFn: async (): Promise<ApplicationQueryResult> => {
        try {
          const response = await apiClient.get<
            ApiPlatformCollectionResponse<MeetingAnnouncementApplicationView>
          >(`/meeting-announcements/${announcement.id}/applications`);
          const items = extractCollectionItems(response.data);
          return {
            announcementId: announcement.id,
            applications: Array.isArray(items) ? items : [],
          };
        } catch (error) {
          console.warn(`Failed to fetch applications for announcement ${announcement.id}:`, error);
          return {
            announcementId: announcement.id,
            applications: [],
          };
        }
      },
      enabled: apiAnnouncements.length > 0,
      staleTime: 30000, // Cache for 30 seconds
    })),
  });

  // Build a map of application counts per announcement
  const applicationCountsMap = useMemo(() => {
    const countsMap: Record<string, AnnouncementApplicationCounts> = {};

    applicationQueries.forEach((query) => {
      if (query.data) {
        const { announcementId, applications } = query.data;
        // Ensure applications is an array
        const appsArray = Array.isArray(applications) ? applications : [];
        const confirmedCount = appsArray.filter(
          (app) => app.statusCode === 'accepted' || app.hostConfirmed
        ).length;

        countsMap[announcementId] = {
          totalApplications: appsArray.length,
          confirmedApplications: confirmedCount,
        };
      }
    });

    return countsMap;
  }, [applicationQueries]);

  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
  const subtextColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;

  const scrollViewContentStyle = { paddingTop: 24, paddingBottom: 100, paddingHorizontal: 16 };

  // Filter announcements based on active filter
  const filteredAnnouncements = useMemo(() => {
    if (activeFilter === 'active') {
      return apiAnnouncements
        .filter(isAnnouncementActive)
        .map((announcement) =>
          mapAnnouncementToCard(announcement, applicationCountsMap[announcement.id])
        );
    } else {
      return apiAnnouncements
        .filter(isAnnouncementArchived)
        .map((announcement) =>
          mapAnnouncementToCard(announcement, applicationCountsMap[announcement.id])
        );
    }
  }, [activeFilter, apiAnnouncements, applicationCountsMap]);

  // Count announcements
  const activeCount = useMemo(() => 
    apiAnnouncements.filter(isAnnouncementActive).length, 
    [apiAnnouncements]
  );
  
  const archiveCount = useMemo(() => 
    apiAnnouncements.filter(isAnnouncementArchived).length, 
    [apiAnnouncements]
  );

  const handleRefresh = useCallback(() => {
    refetch();
    // Also invalidate application count queries to refresh counts
    queryClient.invalidateQueries({ queryKey: ['applicationCounts'] });
  }, [refetch, queryClient]);

  const handleAnnouncementPress = useCallback((announcementId: string) => {
    // Find the full announcement object
    const announcement = apiAnnouncements.find(a => a.id === announcementId);
    
    // Navigate to applications list screen (for owner's announcements)
    rootNav.navigate('AnnouncementApplications', {
      announcementId,
      announcement, // Pass full object to avoid refetch
    });
  }, [apiAnnouncements, rootNav]);

  const handleHeartPress = useCallback((announcementId: string) => {
    // Find the full announcement object
    const announcement = apiAnnouncements.find(a => a.id === announcementId);
    
    // Navigate to applications list screen
    rootNav.navigate('AnnouncementApplications', {
      announcementId,
      announcement, // Pass full object to avoid refetch
    });
  }, [apiAnnouncements, rootNav]);

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        {(Platform.OS === 'web' && !isMobileLayout) ? (
          <ScreenHeader
            title="Moje ogłoszenia"
            onBackPress={() => rootNavigation.goBack()}
          />
        ) : (
          <SvgBackground
            offsetLeft={-30}
            offsetTop={-250}
            height={180}
            variant="flat"
            borderBottomLeftRadius={24}
            borderBottomRightRadius={24}
          >
            <XStack
              paddingTop="$10"
              paddingHorizontal="$4"
              paddingBottom="$3"
              alignItems="center"
              space="$3"
              width="100%"
            >
              <TouchableOpacity onPress={() => rootNavigation.goBack()}>
                <ArrowLeft size={24} color="white" />
              </TouchableOpacity>
              <Text
                fontSize={28}
                fontWeight="bold"
                color="white"
                fontFamily="Poppins"
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.8}
                flexShrink={1}
              >
                Moje ogłoszenia
              </Text>
            </XStack>
          </SvgBackground>
        )}

        <YStack flex={1}>
        {/* Filter Buttons */}
        <XStack paddingHorizontal="$4" paddingVertical="$3" gap="$3">
          <FilterButton
            label="Aktywne"
            count={activeCount}
            isActive={activeFilter === 'active'}
            onPress={() => setActiveFilter('active')}
            isDark={isDark}
            textColor={textColor}
          />
          <FilterButton
            label="Archiwum"
            count={archiveCount}
            isActive={activeFilter === 'archive'}
            onPress={() => setActiveFilter('archive')}
            isDark={isDark}
            textColor={textColor}
          />
        </XStack>

        {isLoading ? (
          <YStack flex={1} alignItems="center" justifyContent="center">
            <ActivityIndicator size="large" color={COLORS.PRIMARY} />
            <Text fontSize={14} color={textColor} opacity={0.7} marginTop="$3">
              Ładowanie ogłoszeń...
            </Text>
          </YStack>
        ) : isError ? (
          <YStack flex={1} alignItems="center" justifyContent="center" gap="$3">
            <Text fontSize={16} color={textColor} opacity={0.7}>
              Nie udało się pobrać ogłoszeń
            </Text>
            <TouchableOpacity onPress={handleRefresh}>
              <Text fontSize={14} color={COLORS.PRIMARY}>
                Spróbuj ponownie
              </Text>
            </TouchableOpacity>
          </YStack>
        ) : (
          <ScrollView
            contentContainerStyle={scrollViewContentStyle}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={isRefetching}
                onRefresh={handleRefresh}
                tintColor={COLORS.PRIMARY}
              />
            }
          >
            <YStack space="$3">
              {filteredAnnouncements.length > 0 ? (
                filteredAnnouncements.map((announcement) => (
                  <AnnouncementCard
                    key={announcement.id}
                    announcement={announcement}
                    isDark={isDark}
                    textColor={textColor}
                    subtextColor={subtextColor}
                    newApplicationsCount={getNewCountForAnnouncement(announcement.id)}
                    onPress={() => handleAnnouncementPress(announcement.id)}
                    onHeartPress={() => handleHeartPress(announcement.id)}
                  />
                ))
              ) : (
                <YStack alignItems="center" paddingVertical="$8">
                  <Text fontSize={16} color={subtextColor}>
                    {activeFilter === 'active' 
                      ? 'Nie masz aktywnych ogłoszeń' 
                      : 'Brak zarchiwizowanych ogłoszeń'}
                  </Text>
                </YStack>
              )}
            </YStack>
          </ScrollView>
        )}
      </YStack>
      </YStack>
    </Screen>
  );
};

export default MyAnnouncementsScreen;

